import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import {
  Card,
  CardHeader,
  CardContent,
  Flex,
  Heading,
  Text,
  Button,
  Box,
  Select,
  DataTable,
  LoadingOrbit,
  DateRangePicker,
  TagBadge,
  type SelectOption,
  badgeVariantToTagColor,
} from '@chatsdet/ui';
import { parseDate, type DateValue } from '@internationalized/date';
import { X } from '@phosphor-icons/react';
import type { Pagination } from '@chatsdet/types';
import { API_URL } from '../../config.js';
import { superadminAgentRunsRoute, type AgentRunsSearchParams } from '../../router.js';
import { CopyablePre } from './CopyButton';

type AgentRunStatus = 'running' | 'succeeded' | 'failed';

type AgentRunLogRecord = {
  id: string;
  org: { id: string; name: string | null } | null;
  label: string;
  status: AgentRunStatus;
  modelId: string | null;
  toolsAvailable: string[];
  totalSteps: number;
  tokensPrompt: number | null;
  tokensCompletion: number | null;
  tokensTotal: number | null;
  finishReason: string | null;
  errorMessage: string | null;
  metadata: Record<string, unknown> | null;
  startedAt: string;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

type AgentRunLogSummaryItem = {
  label: string;
  modelId: string | null;
  tokensPrompt: number;
  tokensCompletion: number;
  tokensTotal: number;
  count: number;
  statusCounts: {
    running: number;
    succeeded: number;
    failed: number;
  };
};

type AgentRunLogSummary = {
  byLabelAndModel: AgentRunLogSummaryItem[];
};

type AgentRunLogResponse = {
  ok: true;
  data: AgentRunLogRecord[];
  pagination: Pagination;
  summary?: AgentRunLogSummary;
};

type FilterOptionsResponse = {
  ok: true;
  labels: string[];
  organizations: Array<{ id: string; name: string | null }>;
};

const STATUS_OPTIONS: SelectOption[] = [
  { id: 'all', label: 'All statuses' },
  { id: 'running', label: 'Running' },
  { id: 'succeeded', label: 'Succeeded' },
  { id: 'failed', label: 'Failed' },
];

const STATUS_VARIANTS: Record<AgentRunStatus, 'warning' | 'success' | 'destructive'> = {
  running: 'warning',
  succeeded: 'success',
  failed: 'destructive',
};

const STATUS_TEXT_COLORS: Record<AgentRunStatus, string> = {
  running: 'text-warning',
  succeeded: 'text-success',
  failed: 'text-destructive',
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
};

const formatDuration = (start?: string, end?: string | null) => {
  if (!start) return '—';
  const startDate = new Date(start);
  const endDate = end ? new Date(end) : new Date();
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return '—';
  const delta = Math.max(endDate.getTime() - startDate.getTime(), 0);
  const seconds = Math.round(delta / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

const formatTokens = (value?: number | null) => {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString();
};

export function AgentRunLogsTab() {
  const navigate = useNavigate();
  const search = useSearch({ from: superadminAgentRunsRoute.id });
  const pageSize = 20;

  const labelFilter = search.label ?? '';
  const orgFilter = search.orgId ?? '';
  const jobIdFilter = search.jobId ?? '';
  const statusFilter = search.status ?? 'all';
  const startDateFilter = search.startDate;
  const endDateFilter = search.endDate;
  const page = search.page ?? 1;

  // Convert ISO string to DateValue for the picker
  const parseDateValue = (isoString?: string): DateValue | null => {
    if (!isoString) return null;
    try {
      // Extract just the date part (YYYY-MM-DD) from ISO string
      const datePart = isoString.split('T')[0];
      if (!datePart) return null;
      return parseDate(datePart);
    } catch {
      return null;
    }
  };

  // Convert DateValue to ISO datetime string for URL
  const dateValueToIso = (date: DateValue | null): string | undefined => {
    if (!date) return undefined;
    return `${date.toString()}T00:00:00.000Z`;
  };

  const dateRangeValue = React.useMemo(() => {
    const start = parseDateValue(startDateFilter);
    const end = parseDateValue(endDateFilter);
    if (!start && !end) return null;
    return { start: start!, end: end! };
  }, [startDateFilter, endDateFilter]);

  const updateSearch = (updates: Partial<AgentRunsSearchParams>) => {
    const newSearch = { ...search, ...updates };
    // Reset page when filters change (unless page is being updated)
    if (!('page' in updates)) {
      newSearch.page = 1;
    }
    // Clean up undefined/empty values
    const cleanSearch = Object.fromEntries(
      Object.entries(newSearch).filter(([, v]) => v !== undefined && v !== '' && v !== 'all')
    );
    navigate({ to: superadminAgentRunsRoute.to, search: cleanSearch });
  };

  const filterOptionsQuery = useQuery({
    queryKey: ['superadmin-agent-runs-filter-options'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/superadmin/agent-runs/filter-options`, {
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error('Failed to load filter options');
      }
      return (await res.json()) as FilterOptionsResponse;
    },
    staleTime: 60000, // Cache for 1 minute
  });

  const labelOptions: SelectOption[] = React.useMemo(() => {
    const options: SelectOption[] = [{ id: 'all', label: 'All labels' }];
    if (filterOptionsQuery.data?.labels) {
      filterOptionsQuery.data.labels.forEach((label) => {
        options.push({ id: label, label });
      });
    }
    return options;
  }, [filterOptionsQuery.data?.labels]);

  const orgOptions: SelectOption[] = React.useMemo(() => {
    const options: SelectOption[] = [{ id: 'all', label: 'All organizations' }];
    if (filterOptionsQuery.data?.organizations) {
      filterOptionsQuery.data.organizations.forEach((org) => {
        options.push({ id: org.id, label: org.name ?? org.id });
      });
    }
    return options;
  }, [filterOptionsQuery.data?.organizations]);

  const agentRunsQuery = useQuery({
    queryKey: [
      'superadmin-agent-runs',
      orgFilter,
      labelFilter,
      statusFilter,
      jobIdFilter,
      startDateFilter,
      endDateFilter,
      page,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('pageSize', pageSize.toString());
      if (orgFilter.length > 0) {
        params.set('orgId', orgFilter);
      }
      if (labelFilter.length > 0) {
        params.set('label', labelFilter);
      }
      if (statusFilter !== 'all') {
        params.set('status', statusFilter);
      }
      if (jobIdFilter.length > 0) {
        params.set('jobId', jobIdFilter);
      }
      if (startDateFilter) {
        params.set('startDate', startDateFilter);
      }
      if (endDateFilter) {
        params.set('endDate', endDateFilter);
      }

      const res = await fetch(`${API_URL}/superadmin/agent-runs?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error('Failed to load agent run logs');
      }
      return (await res.json()) as AgentRunLogResponse;
    },
  });

  const columns = React.useMemo<ColumnDef<StockFeatures, AgentRunLogRecord>[]>(() => {
    return [
      {
        header: 'Label',
        accessorKey: 'label',
        size: 280,
        enableSorting: false,
        cell: ({ row }) => {
          const jobId =
            row.original.metadata && typeof row.original.metadata.jobId === 'string'
              ? row.original.metadata.jobId
              : null;
          const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();
          return (
            <Flex direction="column" gap="xs" className="min-w-0">
              <Text weight="medium">{row.original.label}</Text>
              {jobId && (
                <Text size="xs" className="text-muted-foreground">
                  Job:{' '}
                  <span
                    style={{ userSelect: 'text', cursor: 'text' }}
                    onMouseDown={stopPropagation}
                    onClick={stopPropagation}
                  >
                    {jobId}
                  </span>
                </Text>
              )}
              <Box className="h-px bg-border my-1" />
              {row.original.org ? (
                <>
                  <Text size="xs" weight="medium">
                    {row.original.org.name ?? 'Unnamed org'}
                  </Text>
                  <Text size="xs" className="text-muted-foreground">
                    <span
                      style={{ userSelect: 'text', cursor: 'text' }}
                      onMouseDown={stopPropagation}
                      onClick={stopPropagation}
                    >
                      {row.original.org.id}
                    </span>
                  </Text>
                </>
              ) : (
                <Text size="xs" className="text-muted-foreground">
                  No organization
                </Text>
              )}
            </Flex>
          );
        },
      },
      {
        header: 'Metadata',
        accessorKey: 'metadata',
        size: 300,
        enableSorting: false,
        cell: ({ row }) => {
          if (!row.original.metadata) {
            return <Text className="text-muted-foreground">—</Text>;
          }
          const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();
          const metadataText = JSON.stringify(row.original.metadata, null, 2);
          return (
            <CopyablePre value={metadataText} label="metadata" className="max-w-[280px]">
              <pre
                className="text-xs text-muted-foreground whitespace-pre-wrap break-all font-mono bg-muted/30 rounded px-1 py-0.5"
                style={{ userSelect: 'text', cursor: 'text' }}
                onMouseDown={stopPropagation}
                onClick={stopPropagation}
              >
                {metadataText}
              </pre>
            </CopyablePre>
          );
        },
      },
      {
        header: 'Model / Tools',
        accessorKey: 'modelId',
        enableSorting: false,
        cell: ({ row }) => (
          <Flex direction="column">
            <Text weight="medium">{row.original.modelId ?? 'unknown'}</Text>
            <Text size="xs" className="text-muted-foreground">
              {row.original.toolsAvailable.length > 0
                ? row.original.toolsAvailable.join(', ')
                : 'No tools'}
            </Text>
          </Flex>
        ),
      },
      {
        header: 'Steps / Tokens',
        accessorKey: 'totalSteps',
        enableSorting: false,
        cell: ({ row }) => (
          <Flex direction="column">
            <Text weight="medium">{row.original.totalSteps} steps</Text>
            <Text size="xs" className="text-muted-foreground">
              prompt {formatTokens(row.original.tokensPrompt)} • completion{' '}
              {formatTokens(row.original.tokensCompletion)} • total{' '}
              {formatTokens(row.original.tokensTotal)}
            </Text>
          </Flex>
        ),
      },
      {
        header: 'Status',
        accessorKey: 'status',
        size: 100,
        enableSorting: false,
        cell: ({ row }) => (
          <Flex direction="column" gap="xs">
            <TagBadge
              name={row.original.status}
              color={badgeVariantToTagColor(STATUS_VARIANTS[row.original.status])}
              hideDot
            />
            {row.original.finishReason ? (
              <Text size="xs" className="text-muted-foreground">
                Finish: {row.original.finishReason}
              </Text>
            ) : null}
            {row.original.errorMessage ? (
              <Text size="xs" className="text-destructive">
                {row.original.errorMessage}
              </Text>
            ) : null}
          </Flex>
        ),
      },
      {
        header: 'Timing',
        accessorKey: 'startedAt',
        enableSorting: false,
        cell: ({ row }) => (
          <Flex direction="column">
            <Text weight="medium">{formatDateTime(row.original.startedAt)}</Text>
            <Text size="xs" className="text-muted-foreground">
              Duration: {formatDuration(row.original.startedAt, row.original.completedAt)}
            </Text>
          </Flex>
        ),
      },
    ];
  }, []);

  const summaryColumns = React.useMemo<ColumnDef<StockFeatures, AgentRunLogSummaryItem>[]>(() => {
    return [
      {
        header: 'Label',
        accessorKey: 'label',
        meta: { flex: 2, minWidth: 150 },
        cell: ({ row }) => <Text weight="medium">{row.original.label}</Text>,
      },
      {
        header: 'Model',
        accessorKey: 'modelId',
        meta: { flex: 2, minWidth: 180 },
        cell: ({ row }) => <Text>{row.original.modelId ?? 'unknown'}</Text>,
      },
      {
        header: 'Prompt',
        accessorKey: 'tokensPrompt',
        meta: { flex: 0.75, maxWidth: 135 },
        cell: ({ row }) => <Text>{formatTokens(row.original.tokensPrompt)}</Text>,
      },
      {
        header: 'Completion',
        accessorKey: 'tokensCompletion',
        meta: { flex: 0.75, maxWidth: 135 },
        cell: ({ row }) => <Text>{formatTokens(row.original.tokensCompletion)}</Text>,
      },
      {
        header: 'Total',
        accessorKey: 'tokensTotal',
        meta: { flex: 0.75, maxWidth: 135 },
        cell: ({ row }) => <Text weight="medium">{formatTokens(row.original.tokensTotal)}</Text>,
      },
      {
        header: () => (
          <Flex direction="column" gap="xs">
            <span>Runs</span>
            <span className="text-xs font-normal text-muted-foreground">
              (<span className={STATUS_TEXT_COLORS.running}>run</span>
              {' / '}
              <span className={STATUS_TEXT_COLORS.succeeded}>pass</span>
              {' / '}
              <span className={STATUS_TEXT_COLORS.failed}>fail</span>)
            </span>
          </Flex>
        ),
        accessorKey: 'count',
        cell: ({ row }) => {
          const { running, succeeded, failed } = row.original.statusCounts;
          return (
            <Flex gap="xs" align="center">
              <Text weight="medium">{row.original.count}</Text>
              <Text size="xs" className="text-muted-foreground">
                (<span className={STATUS_TEXT_COLORS.running}>{running}</span>
                {' / '}
                <span className={STATUS_TEXT_COLORS.succeeded}>{succeeded}</span>
                {' / '}
                <span className={STATUS_TEXT_COLORS.failed}>{failed}</span>)
              </Text>
            </Flex>
          );
        },
      },
    ];
  }, []);

  const summary = agentRunsQuery.data?.summary;
  const pagination = agentRunsQuery.data?.pagination;
  const canPrevious = page > 1;
  const canNext = pagination ? page < (pagination.totalPages || 0) : false;

  return (
    <Card padding="lg">
      <CardHeader>
        <Flex align="center" justify="between" wrap gap="md">
          <div>
            <Heading level={3}>Agent Run Logs</Heading>
            <Text size="sm" className="text-muted-foreground mt-1">
              Stream-level telemetry across AI agents.
            </Text>
          </div>
        </Flex>
      </CardHeader>
      <CardContent>
        <Flex gap="md" wrap className="mb-4">
          <Select
            label="Label"
            items={labelOptions}
            selectedKey={labelFilter || 'all'}
            onSelectionChange={(key) =>
              updateSearch({ label: key === 'all' ? undefined : String(key) })
            }
            className="min-w-[200px]"
          />
          <Select
            label="Organization"
            items={orgOptions}
            selectedKey={orgFilter || 'all'}
            onSelectionChange={(key) =>
              updateSearch({ orgId: key === 'all' ? undefined : String(key) })
            }
            className="min-w-[240px]"
          />
          <Box className="min-w-[240px]">
            <Text size="xs" className="text-muted-foreground mb-1 block">
              Job ID
            </Text>
            <input
              value={jobIdFilter}
              onChange={(event) => updateSearch({ jobId: event.target.value || undefined })}
              className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
              placeholder="Filter by job ID (includes children)"
            />
          </Box>
          <Select
            label="Status"
            items={STATUS_OPTIONS}
            selectedKey={statusFilter || 'all'}
            onSelectionChange={(key) =>
              updateSearch({ status: key === 'all' ? undefined : String(key) })
            }
            className="min-w-[160px]"
          />
          <Flex align="end" gap="xs">
            <DateRangePicker
              label="Date Range"
              value={dateRangeValue}
              onChange={(range) => {
                if (range) {
                  updateSearch({
                    startDate: dateValueToIso(range.start),
                    endDate: dateValueToIso(range.end),
                  });
                } else {
                  updateSearch({ startDate: undefined, endDate: undefined });
                }
              }}
              className="min-w-[280px]"
            />
            {dateRangeValue && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => updateSearch({ startDate: undefined, endDate: undefined })}
                className="w-9 p-0"
                aria-label="Clear date range"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </Flex>
        </Flex>

        {summary && summary.byLabelAndModel.length > 0 && (
          <Box className="mb-4 p-4 bg-muted/50 rounded-lg border border-border">
            <Heading level={4} className="mb-3">
              Token Summary
            </Heading>
            <DataTable columns={summaryColumns} data={summary.byLabelAndModel} pageSize={100} />
          </Box>
        )}

        {agentRunsQuery.isLoading ? (
          <Flex align="center" justify="center" className="py-12">
            <LoadingOrbit size="lg" />
          </Flex>
        ) : agentRunsQuery.isError ? (
          <Text className="text-destructive">
            {agentRunsQuery.error instanceof Error
              ? agentRunsQuery.error.message
              : 'Failed to load agent runs'}
          </Text>
        ) : agentRunsQuery.data && agentRunsQuery.data.data.length > 0 ? (
          <DataTable columns={columns} data={agentRunsQuery.data.data} pageSize={100} />
        ) : (
          <Text className="text-muted-foreground py-6">No agent runs found.</Text>
        )}

        <Flex align="center" justify="between" className="mt-4">
          <Text size="sm" className="text-muted-foreground">
            Page {pagination?.page ?? 1} of {pagination?.totalPages ?? 1}
          </Text>
          <Flex gap="sm">
            <Button
              size="sm"
              variant="outline"
              isDisabled={!canPrevious || agentRunsQuery.isFetching}
              onClick={() => updateSearch({ page: Math.max(page - 1, 1) })}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              isDisabled={!canNext || agentRunsQuery.isFetching}
              onClick={() => updateSearch({ page: page + 1 })}
            >
              Next
            </Button>
          </Flex>
        </Flex>
      </CardContent>
    </Card>
  );
}
